import React, { useState, useEffect } from 'react';
import Styles from './Step1Foundation.module.scss';
import { FiUser, FiBriefcase, FiStar } from 'react-icons/fi';

const Step1Foundation = ({ data, onUpdate, onNext, onPrevious, onSaveAndExit, onSaveAndContinue }) => {
  const [brandType, setBrandType] = useState(data.brandType || 'BUSINESS');
  const [name, setName] = useState(data.name || '');
  const [offering, setOffering] = useState(data.offering || '');
  const [marketPositioning, setMarketPositioning] = useState(
    data.marketPositioning || ''
  );
  const [valueProposition, setValueProposition] = useState(
    data.valueProposition || ''
  );
  const [uniqueSellingPoint, setUniqueSellingPoint] = useState(
    data.uniqueSellingPoint || ''
  );

  // Update fields when data prop changes (from Step 2 website analysis)
  useEffect(() => {
    if (data.name) setName(data.name);
    if (data.offering) setOffering(data.offering);
    if (data.marketPositioning) setMarketPositioning(data.marketPositioning);
    if (data.valueProposition) setValueProposition(data.valueProposition);
    if (data.uniqueSellingPoint) setUniqueSellingPoint(data.uniqueSellingPoint);
  }, [data]);

  const handleContinue = () => {
    if (!name.trim() || !offering.trim()) {
      return;
    }

    // Build the current data object
    const currentData = {
      brandType,
      name,
      offering,
      marketPositioning,
      valueProposition,
      uniqueSellingPoint,
    };

    // Update local state
    onUpdate(currentData);

    // Pass data directly to save (avoids async state update issues)
    if (onSaveAndContinue) {
      onSaveAndContinue(currentData);
    } else {
      onNext();
    }
  };

  const isValid = name.trim() && offering.trim();

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Brand Foundation</h2>
        <p className={Styles.subtitle}>
          Start by defining your brand basics to generate your Verbal Identity
        </p>
      </div>

      {/* Form Content */}
      <div className={Styles.formContent}>
        {/* Brand Type Selection */}
        <div className={Styles.section}>
          <label className={Styles.sectionLabel}>Brand Type</label>
          <div className={Styles.brandTypeGrid}>
            {/* Personal Brand Card */}
            <button
              type="button"
              onClick={() => setBrandType('PERSONAL')}
              className={`${Styles.brandTypeCard} ${
                brandType === 'PERSONAL' ? Styles.selected : ''
              }`}
            >
              <div className={Styles.brandTypeContent}>
                <div
                  className={`${Styles.iconWrapper} ${
                    brandType === 'PERSONAL' ? Styles.iconActive : ''
                  }`}
                >
                  <FiUser className={Styles.icon} />
                </div>
                <div className={Styles.brandTypeText}>
                  <div className={Styles.brandTypeName}>Personal Brand</div>
                </div>
              </div>
              {brandType === 'PERSONAL' && (
                <div className={Styles.selectedBadge}>
                  <FiStar />
                </div>
              )}
            </button>

            {/* Business Card */}
            <button
              type="button"
              onClick={() => setBrandType('BUSINESS')}
              className={`${Styles.brandTypeCard} ${
                brandType === 'BUSINESS' ? Styles.selected : ''
              }`}
            >
              <div className={Styles.brandTypeContent}>
                <div
                  className={`${Styles.iconWrapper} ${
                    brandType === 'BUSINESS' ? Styles.iconActive : ''
                  }`}
                >
                  <FiBriefcase className={Styles.icon} />
                </div>
                <div className={Styles.brandTypeText}>
                  <div className={Styles.brandTypeName}>Business</div>
                </div>
              </div>
              {brandType === 'BUSINESS' && (
                <div className={Styles.selectedBadge}>
                  <FiStar />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Brand Name & Offering (2-column layout) */}
        <div className={Styles.twoColumn}>
          <div className={Styles.inputGroup}>
            <label htmlFor="brandName" className={Styles.label}>
              Brand Name <span className={Styles.required}>*</span>
            </label>
            <input
              id="brandName"
              type="text"
              className={Styles.input}
              placeholder="Brand Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className={Styles.inputGroup}>
            <label htmlFor="offering" className={Styles.label}>
              What do you offer? <span className={Styles.required}>*</span>
            </label>
            <input
              id="offering"
              type="text"
              className={Styles.input}
              placeholder="What do you offer?"
              value={offering}
              onChange={(e) => setOffering(e.target.value)}
            />
          </div>
        </div>

        {/* Market Positioning */}
        <div className={Styles.inputGroup}>
          <label htmlFor="marketPositioning" className={Styles.label}>
            <div className={Styles.labelWithIcon}>
              <FiStar className={Styles.sparkle} />
              Market Positioning
            </div>
          </label>
          <textarea
            id="marketPositioning"
            className={Styles.textarea}
            placeholder="Market Positioning"
            rows={4}
            value={marketPositioning}
            onChange={(e) => setMarketPositioning(e.target.value)}
          />
        </div>

        {/* Value Proposition */}
        <div className={Styles.inputGroup}>
          <label htmlFor="valueProposition" className={Styles.label}>
            <div className={Styles.labelWithIcon}>
              <FiStar className={Styles.sparkle} />
              Value Proposition
            </div>
          </label>
          <textarea
            id="valueProposition"
            className={Styles.textarea}
            placeholder="Value Proposition"
            rows={4}
            value={valueProposition}
            onChange={(e) => setValueProposition(e.target.value)}
          />
        </div>

        {/* Unique Selling Point */}
        <div className={Styles.inputGroup}>
          <label htmlFor="uniqueSellingPoint" className={Styles.label}>
            <div className={Styles.labelWithIcon}>
              <FiStar className={Styles.sparkle} />
              Unique Selling Point
            </div>
          </label>
          <textarea
            id="uniqueSellingPoint"
            className={Styles.textarea}
            placeholder="Unique Selling Point"
            rows={4}
            value={uniqueSellingPoint}
            onChange={(e) => setUniqueSellingPoint(e.target.value)}
          />
        </div>
      </div>

      {/* Footer Navigation */}
      <div className={Styles.footer}>
        <button type="button" className={Styles.previousBtn} onClick={onPrevious}>
          Previous
        </button>
        <button type="button" className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 2 of 7</div>
        <button
          type="button"
          className={Styles.continueBtn}
          onClick={handleContinue}
          disabled={!isValid}
        >
          Save & Continue
        </button>
      </div>
    </div>
  );
};

export default Step1Foundation;
